/* File : src/services/api.js */
import axios from 'axios';

// Configuration de base d'Axios
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || '/brightnight/api', // URL de l'API, ajustez selon votre configuration
  timeout: 120000, // Timeout plus long pour les environnements de production
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  }
});

// Données de fallback en cas d'erreur d'API
const mockUsers = [
  {
    id: 1,
    name: 'John Doe',
    phone: '655123456',
    birthYear: 1990,
    passion: 'music',
    isValidated: true,
    registrationDate: '2023-03-01T10:30:00Z',
  },
  {
    id: 2,
    name: 'Alice Smith',
    phone: '677987654',
    birthYear: 1985,
    passion: 'art',
    isValidated: false,
    registrationDate: '2023-03-02T14:15:00Z',
  }
];

const mockTickets = [
  {
    id: 1,
    userId: 1,
    ticketCode: 'GUIN001',
    qrData: 'GUIN001-1-655123456',
  },
  {
    id: 2,
    userId: 2,
    ticketCode: 'GUIN002',
    qrData: 'GUIN002-2-677987654',
  }
];

// Intercepteur pour les requêtes API
api.interceptors.request.use(
  (config) => {
    console.log('API Request:', config.url);
    
    // Aucun token à ajouter
    return config;
  },
  (error) => {
    console.error('API Request Error:', error);
    return Promise.reject(error);
  }
);

// Intercepteur pour gérer les réponses et les erreurs
api.interceptors.response.use(
  (response) => {
    console.log('API Response:', response.config.url, response.status);
    return response;
  },
  (error) => {
    console.error('API Response Error:', error.config?.url, error.response?.status, error.message);
    
    // Gérer les erreurs d'authentification (401)
    if (error.response && error.response.status === 401) {
      // Rediriger vers la page de connexion appropriée
      if (error.config.url.includes('/admin')) {
        localStorage.removeItem('adminToken');
        localStorage.removeItem('adminUser');
        window.location.href = '/brightnight/admin/login';
      } else if (error.config.url.includes('/scan')) {
        localStorage.removeItem('scannerToken');
        localStorage.removeItem('scannerUser');
        window.location.href = '/brightnight/scanner/login';
      }
    }

    return Promise.reject(error);
  }
);

// Fonction utilitaire pour détecter le type d'appareil
const detectDeviceType = () => {
  const ua = navigator.userAgent;
  if (/(tablet|ipad|playbook|silk)|(android(?!.*mobi))/i.test(ua)) {
    return "tablet";
  }
  if (/Mobile|Android|iP(hone|od)|IEMobile|BlackBerry|Kindle|Silk-Accelerated|(hpw|web)OS|Opera M(obi|ini)/.test(ua)) {
    return "mobile";
  }
  return "desktop";
};

// API publique pour les utilisateurs non authentifiés
export const publicApi = {
  
  getLatestEvent: () => {
    console.log('Getting latest event');
    return api.get('/events/latest')
      .catch(error => {
        console.error('Erreur lors de la récupération de l\'événement le plus récent:', error);
        return Promise.reject({
          message: "Impossible de récupérer les événements. Problème de connexion à la base de données.",
          originalError: error.message,
          status: error.response?.status || 500
        });
      });
  },
  
   // Récupérer un événement par ID (conservé pour la compatibilité)
  getEventById: (eventId) => {
    console.log('Getting event by ID:', eventId);
    return api.get(`/events/show/${eventId}`)
      .catch(error => {
        console.error('API error in getEventById:', error);
        return Promise.reject({
          message: "Erreur lors de la récupération des détails de l'événement",
          originalError: error,
          status: error.response?.status || 500
        });
      });
  },
  
  // Récupérer l'événement par ville
  getEventByCity: (city) => {
    console.log('Getting event by city:', city);
    return api.get(`/events/by-city/${city}`)
      .catch(error => {
        console.error('API error in getEventByCity:', error);
        
        // Ne pas utiliser de données mockées, mais propager l'erreur
        return Promise.reject({
          message: "Erreur lors de la récupération de l'événement de la ville",
          originalError: error,
          status: error.response?.status || 500
        });
      });
  },
  
  // Vérification de l'âge - Supporte à la fois l'année seule et la date complète
  verifyAge: (birthDateData) => {
    console.log('Verifying age:', birthDateData);
    
    try {
      return api.post('/verify-age', birthDateData)
        .catch(error => {
          console.warn('API error in verifyAge, using client-side verification', error);
          
          // Fallback en cas d'erreur d'API - Vérification côté client
          if (typeof birthDateData === 'string' || typeof birthDateData === 'number') {
            const currentYear = new Date().getFullYear();
            const birthYear = parseInt(birthDateData);
            return Promise.resolve({
              data: {
                eligible: (currentYear - birthYear) >= 21
              }
            });
          } else {
            // Vérification avec date complète
            const { day, month, year } = birthDateData;
            const birthDate = new Date(year, month - 1, day);
            const today = new Date();
            
            // Calculer l'âge
            let age = today.getFullYear() - birthDate.getFullYear();
            const m = today.getMonth() - birthDate.getMonth();
            
            if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
              age--;
            }
            
            return Promise.resolve({
              data: {
                eligible: age >= 21
              }
            });
          }
        });
    } catch (e) {
      console.error('Error in verifyAge:', e);
      return Promise.reject(e);
    }
  },
  
  // Vérifier si un utilisateur est déjà inscrit
  checkRegistration: (phone) => {
    console.log('Checking registration for phone:', phone);
    return api.post('/check-registration', { phone })
      .catch(error => {
        console.warn('API error in checkRegistration, using mock data', error);
        // Fallback avec données mockées
        const exists = mockUsers.some(user => user.phone === phone);
        return Promise.resolve({
          data: {
            success: true,
            registered: exists
          }
        });
      });
  },
  
  // Enregistrement d'un nouvel utilisateur (version mise à jour sans passion)
  register: (userData) => {
    console.log('Registering user:', userData);
    return api.post('/register', userData)
      .catch(error => {
        console.error('API error in register:', error);
        
        if (!navigator.onLine) {
          // Si hors ligne, stocker la demande pour synchronisation ultérieure
          const registrationRequest = {
            type: 'registration',
            data: userData,
            timestamp: new Date().toISOString()
          };
          
          // Stocker dans IndexedDB pour synchronisation ultérieure
          if (indexedDBService) {
            indexedDBService.offlineQueue.add(registrationRequest);
          }
          
          // Simuler une réponse réussie
          return Promise.resolve({
            data: {
              success: true,
              message: "Inscription enregistrée hors ligne. Votre ticket sera disponible lorsque vous serez à nouveau en ligne.",
              offlineMode: true,
              user: {
                firstName: userData.firstName,
                lastName: userData.lastName,
                phone: userData.phone,
                gender: userData.gender,
                district: userData.district || '',
                birthDate: `${userData.birthYear}-${String(userData.birthMonth).padStart(2, '0')}-${String(userData.birthDay).padStart(2, '0')}`
              },
              ticket: {
                ticketCode: `OFFLINE-${Date.now()}`,
                qrData: `OFFLINE-${userData.phone}-${Date.now()}`,
                offlineGenerated: true
              }
            }
          });
        }
        
        // En ligne mais API en erreur, donc échec complet
        return Promise.reject(error);
      });
  },
  
  // Scanner un QR code (accessible sans authentification)
  scanQRCode: (qrData) => {
    console.log('Scanning QR code:', qrData);
    const scannerId = localStorage.getItem('scannerUser') ? JSON.parse(localStorage.getItem('scannerUser')).username : 'anonymous';
    return api.post('/scan', { qrData, scannerId, isOffline: !navigator.onLine })
      .catch(error => {
        console.warn('API error in scanQRCode, using mock data', error);
        // Fallback avec données mockées
        
        if (!navigator.onLine) {
          const offlineScan = {
            id: Date.now(),
            qrData,
            scanTime: new Date().toISOString(),
            isOffline: true,
            synced: false,
            scannerId
          };
          
          // Stocker dans le localStorage
          const storedScans = localStorage.getItem('offlineScans');
          const offlineScans = storedScans ? JSON.parse(storedScans) : [];
          offlineScans.push(offlineScan);
          localStorage.setItem('offlineScans', JSON.stringify(offlineScans));
          
          // Simuler une réponse réussie
          return Promise.resolve({
            success: true,
            message: "Scan enregistré en mode hors ligne",
            entry: {
              id: offlineScan.id,
              ticketId: 0,
              userId: 0,
              name: "Utilisateur (hors ligne)",
              phone: qrData.split('-')[2] || '',
              ticketCode: qrData.split('-')[0] || '',
              scanTime: offlineScan.scanTime,
              isOffline: true
            }
          });
        }
       
      });
  },
  
  // Synchroniser les scans hors ligne
  syncOfflineScans: (scans, scannerId = 'anonymous') => {
    console.log('Syncing offline scans:', scans.length, 'scanner:', scannerId);
    return api.post('/sync-offline-scans', { scans, scannerId })
      .catch(error => {
        console.warn('API error in syncOfflineScans, using mock data', error);
        // Fallback avec données mockées
        return Promise.resolve({
          success: true,
          message: `${scans.length} scan(s) synchronisé(s) avec succès`,
          processed: scans.map(scan => ({
            id: scan.id,
            qrData: scan.qrData,
            status: 'synced'
          })),
          failed: []
        });
      });
  },
  
  // Récupérer les entrées récentes
  getRecentEntries: (limit = 10) => {
    console.log('Getting recent entries:', limit);
    return api.get('/scanner/recent-entries', { params: { limit } })
      .catch(error => {
        console.warn('API error in getRecentEntries, using mock data', error);
        // Fallback avec données mockées
        
        // Générer des données aléatoires pour les tests
        const mockEntries = [];
        const now = new Date();
        
        for (let i = 0; i < limit; i++) {
          const entryTime = new Date(now.getTime() - i * 5 * 60000); // 5 minutes d'intervalle
          mockEntries.push({
            id: Date.now() - i,
            name: `Utilisateur ${i + 1}`,
            phone: `65${Math.floor(1000000 + Math.random() * 9000000)}`,
            ticketCode: `GUIN${1000 + i}`,
            scanTime: entryTime.toISOString(),
            isOffline: Math.random() > 0.8 // 20% de chance d'être hors ligne
          });
        }
        
        return Promise.resolve({
          success: true,
          entries: mockEntries
        });
      });
  },
  
  // Récupération des informations d'un utilisateur par téléphone
  getUserByPhone: (phone) => {
    console.log('Getting user by phone:', phone);
    return api.get(`/user/${phone}`)
      .catch(error => {
        console.warn('API error in getUserByPhone, using mock data', error);
        // Fallback avec données mockées
        const user = mockUsers.find(u => u.phone === phone);
        const ticket = user ? mockTickets.find(t => t.userId === user.id) : null;
        
        if (user && ticket) {
          return Promise.resolve({
            data: {
              success: true,
              user: user,
              ticket: ticket
            }
          });
        } else {
          return Promise.reject({ 
            response: { 
              status: 404, 
              data: { message: 'Utilisateur non trouvé' } 
            } 
          });
        }
      });
  },
  
  // Sauvegarder l'abonnement aux notifications
  saveNotificationSubscription: (phone, subscription) => {
    console.log('Saving notification subscription for phone:', phone);
    return api.post('/save-notification-subscription', { 
      phone, 
      subscription,
      deviceType: detectDeviceType()
    })
    .catch(error => {
      console.error('API error in saveNotificationSubscription:', error);
      return Promise.reject(error);
    });
  }
};

// API pour le scanner
export const scannerApi = {
  // Scan d'un QR code
  scanQRCode: (qrData) => {
    console.log('Scanning QR code:', qrData);
    return api.post('/scan', { qrData })
      .catch(error => {
        console.warn('API error in scanQRCode, using mock data', error);
        // Fallback avec données mockées
        const ticket = mockTickets.find(t => t.qrData === qrData);
        
        if (!ticket) {
          return Promise.reject({
            response: {
              status: 404,
              data: { message: 'Ticket non trouvé' }
            }
          });
        }
        
        const user = mockUsers.find(u => u.id === ticket.userId);
        
        if (!user.isValidated) {
          return Promise.reject({
            response: {
              status: 400,
              data: { message: 'Ticket non validé' }
            }
          });
        }
        
        return Promise.resolve({
          data: {
            success: true,
            entry: {
              id: Math.floor(Math.random() * 1000),
              ticketId: ticket.id,
              userId: user.id,
              name: user.name,
              phone: user.phone,
              ticketCode: ticket.ticketCode,
              scanTime: new Date().toISOString()
            }
          }
        });
      });
  },
  
  // Synchronisation des scans hors ligne
  syncOfflineScans: (scans) => {
    console.log('Syncing offline scans:', scans.length);
    return api.post('/sync-offline-scans', { scans })
      .catch(error => {
        console.warn('API error in syncOfflineScans, using mock data', error);
        // Fallback avec données mockées
        return Promise.resolve({
          data: {
            success: true,
            synced: scans.length,
            message: `${scans.length} scan(s) synchronisé(s) avec succès`
          }
        });
      });
  },
  
  // Récupération des entrées récentes
  getRecentEntries: (limit = 10) => {
    console.log('Getting recent entries, limit:', limit);
    return api.get('/scanner/recent-entries', { params: { limit } })
      .catch(error => {
        console.warn('API error in getRecentEntries, using mock data', error);
        // Fallback avec données mockées
        return Promise.resolve({
          data: {
            success: true,
            entries: []
          }
        });
      });
  }
};

export default api;